
import { Database, BarChart3, Zap } from "lucide-react";

const HowItWorksSection = () => {
  const steps = [
    {
      step: "01",
      title: "Collect Real-Time Data",
      description: "Gather insights from social media, mobile panels, and verified citizen databases across 100+ countries",
      icon: <Database className="h-8 w-8 text-white" />,
      color: "bg-nation-blue"
    },
    {
      step: "02", 
      title: "Analyze Public Mood",
      description: "Our AI-driven dashboards process sentiment, identify trends, and predict public reaction patterns",
      icon: <BarChart3 className="h-8 w-8 text-white" />,
      color: "bg-nation-teal"
    },
    {
      step: "03",
      title: "Act Immediately",
      description: "Receive instant alerts, access detailed reports, and integrate data via API for immediate decision-making",
      icon: <Zap className="h-8 w-8 text-white" />,
      color: "bg-nation-purple"
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Three simple steps to transform public opinion into actionable intelligence
            </p>
          </div>

          <div className="relative">
            {/* Connection lines for desktop */}
            <div className="hidden lg:block absolute top-24 left-1/2 transform -translate-x-1/2 w-full max-w-4xl">
              <div className="flex justify-between items-center">
                <div className="w-1/3 h-0.5 bg-gradient-to-r from-nation-blue to-nation-teal"></div>
                <div className="w-1/3 h-0.5 bg-gradient-to-r from-nation-teal to-nation-purple"></div>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-12 lg:gap-8">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  {/* Step indicator */}
                  <div className="flex flex-col items-center text-center">
                    {/* Icon circle */}
                    <div className={`${step.color} w-20 h-20 rounded-full flex items-center justify-center mb-6 shadow-lg relative z-10`}>
                      {step.icon}
                    </div>

                    {/* Step number */}
                    <div className="text-4xl font-bold text-gray-300 dark:text-gray-600 mb-4">
                      {step.step}
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                      {step.title}
                    </h3>
                    
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed max-w-sm">
                      {step.description}
                    </p>
                  </div>

                  {/* Mobile connection line */}
                  {index < steps.length - 1 && (
                    <div className="lg:hidden flex justify-center mt-8 mb-4">
                      <div className="w-0.5 h-8 bg-gradient-to-b from-gray-300 to-gray-400"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Additional info */}
          <div className="mt-16 text-center">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-gray-700 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Ready to Get Started?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Join thousands of organizations already using NationCard™ to make data-driven decisions
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-nation-teal hover:bg-nation-teal-dark text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg">
                  Request a Demo
                </button>
                <button className="border border-nation-blue text-nation-blue hover:bg-nation-blue hover:text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300">
                  View Pricing
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
